import React, { useMemo, useState, useEffect } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Home,
  ClipboardList,
  Factory,
  FlaskConical,
  Wrench,
  DoorOpen,
  Boxes,
  BarChart3,
  Menu,
  LogOut,
  ChevronDown,
    Snowflake,
} from "lucide-react";
import { Avatar, AvatarFallback } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "./components/ui/dropdown-menu";
import { cn } from "./lib/utils";
import { clearLegacySession } from "./lib/legacySession";
import logo from "./img/logo.png";

const SIDER_WIDTH = 262;
const SIDER_COLLAPSED = 80;
const HEADER_HEIGHT = 64;

const PATHS = {
  inicio: "/dashboard",
  aplicaciones: "/dashboard/aplicaciones-fertilizantes",
  produccion: "/dashboard/planta/orden-proceso",
  produccionRecepcion: "/dashboard/planta/resumen-recepciones",
  analisisCalidad: "/dashboard/calidad/analisis",
  mantenedores: "/dashboard/mantenedores",
  portalProductor: "/dashboard/portal-productor",
  produccionEstimaciones: "/dashboard/produccion/estimaciones",
  bodegaConsolidado: "/dashboard/bodega/consolidado-existencias",
   camarasFrio: "/dashboard/bodega/camaras-frio",
  analisisPowerBi: "/dashboard/powerbi/analisis-powerbi",
};

const NavItem = ({ to, icon: Icon, label, active, collapsed }) => (
  <Link
    to={to}
    className={cn(
      "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
      active
        ? "bg-primary-light text-primary font-semibold"
        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
    )}
    title={collapsed ? label : undefined}
  >
    <Icon size={20} className="shrink-0" />
    {!collapsed && <span className="truncate">{label}</span>}
  </Link>
);

const SubMenu = ({ icon: Icon, label, children, openKey, onToggle, collapsed, selectedKey }) => {
  const isOpen = !collapsed && openKey;
  return (
    <div>
      <button
        type="button"
        onClick={() => !collapsed && onToggle()}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors",
          "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
        )}
        title={collapsed ? label : undefined}
      >
        <Icon size={20} className="shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left truncate">{label}</span>
            <ChevronDown
              size={16}
              className={cn("transition-transform shrink-0", isOpen && "rotate-180")}
            />
          </>
        )}
      </button>
      {isOpen && (
        <div className="ml-8 mt-0.5 space-y-0.5 border-l border-gray-100 pl-3">
          {children}
        </div>
      )}
    </div>
  );
};

const SubItem = ({ to, label, active }) => (
  <Link
    to={to}
    className={cn(
      "block px-3 py-2 rounded-lg text-sm transition-colors",
      active
        ? "bg-primary-light text-primary font-semibold"
        : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
    )}
  >
    {label}
  </Link>
);

const Dashboard = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const location = useLocation();

  const currentSider = collapsed ? SIDER_COLLAPSED : SIDER_WIDTH;

  const toggleCollapse = () => setCollapsed((v) => !v);

  useEffect(() => {
    const p = (location.pathname || "").toLowerCase();
    if (!collapsed) {
      const keys = [];
      if (p.startsWith("/dashboard/planta")) keys.push("produccion");
      if (p.startsWith("/dashboard/powerbi")) keys.push("powerbi");
      setOpenKeys(keys);
    } else {
      setOpenKeys([]);
    }
  }, [collapsed, location.pathname]);

  const selectedKey = useMemo(() => {
    const p = (location.pathname || "").toLowerCase();
    if (p.startsWith("/dashboard/powerbi/analisis-powerbi")) return "powerbi-analisis";
    if (p === "/dashboard" || p === "/dashboard/home") return "inicio";
    if (p.startsWith("/dashboard/aplicaciones")) return "aplicaciones";
    if (p.startsWith("/dashboard/produccion/estimaciones")) return "estimaciones";
    if (p.startsWith("/dashboard/planta/orden-proceso")) return "produccion-orden";
    if (p.startsWith("/dashboard/planta/resumen-recepciones")) return "produccion-resumen";
    if (p.startsWith("/dashboard/bodega/consolidado-existencias")) return "bodega-consolidado";
    if (p.startsWith("/dashboard/calidad/analisis")) return "analisis-calidad";
    if (p.startsWith("/dashboard/mantenedores")) return "mantenedores";
        if (p.startsWith("/dashboard/bodega/camaras-frio")) return "camaras-frio";
    if (p.startsWith("/dashboard/portal-productor")) return "portal-productor";
    return "";
  }, [location.pathname]);

  const userName = localStorage.getItem("userName") || "Usuario";
  const initials = userName
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const toggleSubMenu = (key) =>
    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  return (
    <div className="flex min-h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside
        className="fixed top-0 left-0 h-screen bg-white border-r border-gray-200 flex flex-col z-30 transition-all duration-300"
        style={{ width: currentSider }}
      >
        {/* Logo */}
        <div className="h-20 flex items-center justify-center px-3 shrink-0">
          <img
            src={logo}
            alt="Logo"
            className="object-contain transition-all duration-300"
            style={{
              height: collapsed ? 36 : 54,
              maxWidth: collapsed ? 54 : 180,
              width: "100%",
            }}
          />
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 pb-4 space-y-0.5">
          <NavItem
            to={PATHS.inicio}
            icon={Home}
            label="Inicio"
            active={selectedKey === "inicio"}
            collapsed={collapsed}
          />
          <NavItem
            to={PATHS.aplicaciones}
            icon={ClipboardList}
            label="Aplicaciones"
            active={selectedKey === "aplicaciones"}
            collapsed={collapsed}
          />
          <NavItem
            to={PATHS.produccionEstimaciones}
            icon={Factory}
            label="Estimado de Producción"
            active={selectedKey === "estimaciones"}
            collapsed={collapsed}
          />

          <SubMenu
            icon={Factory}
            label="Producción"
            openKey={openKeys.includes("produccion")}
            onToggle={() => toggleSubMenu("produccion")}
            collapsed={collapsed}
            selectedKey={selectedKey}
          >
            <SubItem
              to={PATHS.produccion}
              label="Resumen de Procesos"
              active={selectedKey === "produccion-orden"}
            />
            <SubItem
              to={PATHS.produccionRecepcion}
              label="Recepciones Materia Prima"
              active={selectedKey === "produccion-resumen"}
            />
          </SubMenu>

          <NavItem
            to={PATHS.bodegaConsolidado}
            icon={Boxes}
            label="Bodega"
            active={selectedKey === "bodega-consolidado"}
            collapsed={collapsed}
          />

           <NavItem
            to={PATHS.camarasFrio}
            icon={Snowflake}
            label="Cámaras de Frío"
            active={selectedKey === "camaras-frio"}
            collapsed={collapsed}
          />
          <NavItem
            to={PATHS.analisisCalidad}
            icon={FlaskConical}
            label="Análisis de Calidad"
            active={selectedKey === "analisis-calidad"}
            collapsed={collapsed}
          />
          <NavItem
            to={PATHS.mantenedores}
            icon={Wrench}
            label="Mantenedores"
            active={selectedKey === "mantenedores"}
            collapsed={collapsed}
          />

          <SubMenu
            icon={BarChart3}
            label="Dashboards Power BI"
            openKey={openKeys.includes("powerbi")}
            onToggle={() => toggleSubMenu("powerbi")}
            collapsed={collapsed}
            selectedKey={selectedKey}
          >
            <SubItem
              to={PATHS.analisisPowerBi}
              label="Análisis de Calidad"
              active={selectedKey === "powerbi-analisis"}
            />
          </SubMenu>

          <NavItem
            to={PATHS.portalProductor}
            icon={DoorOpen}
            label="Portal Productor"
            active={selectedKey === "portal-productor"}
            collapsed={collapsed}
          />
        </nav>
      </aside>

      {/* Right area */}
      <div
        className="flex flex-col flex-1 transition-all duration-300"
        style={{ marginLeft: currentSider }}
      >
        {/* Header */}
        <header
          className="fixed top-0 right-0 z-20 flex items-center justify-between bg-white border-b border-gray-200"
          style={{
            height: HEADER_HEIGHT,
            left: currentSider,
            transition: "left .3s",
          }}
        >
          <button
            type="button"
            onClick={toggleCollapse}
            className="ml-4 p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            aria-label="Toggle sidebar"
          >
            <Menu size={20} />
          </button>

          <div className="flex items-center gap-4 mr-5">
            <Separator orientation="vertical" className="h-6" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-gray-50 transition-colors"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="text-xs bg-primary text-white">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-gray-700">{userName}</span>
                  <ChevronDown size={14} className="text-gray-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={6}>
                <DropdownMenuItem
                  onClick={() => {
                    clearLegacySession();
                    window.location.href = "/login";
                  }}
                  className="gap-2 text-red-600 hover:bg-red-50"
                >
                  <LogOut size={14} />
                  Cerrar Sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Content */}
        <main
          className="flex-1 overflow-auto bg-gray-50 p-5"
          style={{ marginTop: HEADER_HEIGHT }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
